import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { TASK_STATUS } from 'modules/enumerations/enumerationIds';
import { TASK_STATUS_FILTER_OPTIONS, TASK_STATUS_TITLES, matchesStatusFilter } from 'modules/dashboard/taskStatus';
import { formatDateForInput } from 'utilities/common';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import { enumColumn } from 'modules/common/enumTableColumn';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import TaskDueDate from './TaskDueDate.component';

// Status filter value meaning every status; the Status filter is never empty and defaults to Active
const ALL_STATUSES = 'all';

const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS_TITLES).map(([id, title]) => ({ id, title }));

// A task whose importance is not listed shows as High
const IMPORTANCE_LEVELS = [
  { id: '3e340180-e70f-4ec7-9c71-f4e4600f8184', classKey: 'lowPriority', label: 'Low' },
  { id: '7e3faf93-13e3-48fa-815c-edc4a715ab0f', classKey: 'normalPriority', label: 'Normal' },
  { id: 'a9a0fc7-4d4c-41cd-a55b-0d88be85ead8', classKey: 'highPriority', label: 'High' },
];

const getImportanceLevel = task => IMPORTANCE_LEVELS.find(i => i.id === task.taskImportanceId) || IMPORTANCE_LEVELS[2];

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  filterControl: {
    minWidth: 200,
  },
  filterControlSmall: {
    minWidth: 150,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noTasks: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  overdueRow: {
    backgroundColor: theme.palette.error.light + '20', // Light red background
  },
  clickableTitle: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  highPriority: {
    color: theme.palette.error.main,
  },
  normalPriority: {
    color: theme.palette.text.primary,
  },
  lowPriority: {
    color: theme.palette.text.secondary,
  },
  statusActive: {
    color: theme.palette.primary.main,
  },
  statusOverdue: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  statusCompleted: {
    color: theme.palette.success.main,
  },
  statusCanceled: {
    color: theme.palette.text.secondary,
  },
}));

const TasksGrid = ({ tasks, onTaskAction, isLoading, vendors, enumerations, dashboardFilters }) => {
  const classes = useStyles();

  // Filter state
  const [filters, setFilters] = useState({
    taskTypeId: null,
    vendorId: null,
    isVIP: null,
    taskStatusId: TASK_STATUS.ACTIVE,
    taskImportanceId: null,
    serviceId: null,
    dueDate: null,
    showCompleted: false,
    showCanceled: false,
  });

  // Extract enumerations from Redux
  const taskTypes =
    enumerations?.taskTypes && enumerations.taskTypes.length > 0
      ? enumerations.taskTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const taskStatuses =
    enumerations?.taskStatuses && enumerations.taskStatuses.length > 0
      ? enumerations.taskStatuses.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const taskImportances =
    enumerations?.taskImportances && enumerations.taskImportances.length > 0
      ? enumerations.taskImportances.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-111111111111', title: '' }];

  // Task.ServiceId references the AuthCommentService enumeration; inactive services still resolve for display
  const allServices = useMemo(() => enumerations?.authCommentService || [], [enumerations]);
  const services = allServices.filter(s => s.isActive === true);

  // Client-side filtering using useMemo
  const filteredTasks = useMemo(() => {
    // Dashboard-wide Client and Employee/File filters scope the whole table, the toggles below included;
    // task.clientId is the file's ClientRecordId
    let scopedTasks = [...tasks];
    if (dashboardFilters.clientId) {
      scopedTasks = scopedTasks.filter(t => t.clientId === dashboardFilters.clientId);
    }
    if (dashboardFilters.fileId) {
      scopedTasks = scopedTasks.filter(t => t.fileId === dashboardFilters.fileId);
    }

    let result = scopedTasks;

    if (filters.taskTypeId) {
      result = result.filter(t => t.taskTypeId === filters.taskTypeId);
    }
    if (filters.taskStatusId !== ALL_STATUSES) {
      result = result.filter(t => matchesStatusFilter(t, filters.taskStatusId));
    }
    if (filters.taskImportanceId) {
      result = result.filter(t => t.taskImportanceId === filters.taskImportanceId);
    }
    if (filters.serviceId) {
      result = result.filter(t => t.serviceId === filters.serviceId);
    }
    if (filters.vendorId) {
      result = result.filter(t => t.vendorId === filters.vendorId);
    }
    if (filters.isVIP) {
      result = result.filter(t => t.isVIP === true);
    }
    if (filters.dueDate) {
      // dueDate is a full timestamp; compare the same yyyy-MM-dd value the Due Date column shows
      result = result.filter(t => formatDateForInput(t.dueDate) === filters.dueDate);
    }

    // The toggles add every Completed / Canceled task on top of the filtered list, ignoring the filters above
    const matchingTasks = new Set(result);
    return scopedTasks.filter(t =>
      matchingTasks.has(t) ||
      (filters.showCompleted && isIdEqual(t.taskStatusId, TASK_STATUS.COMPLETED)) ||
      (filters.showCanceled && isIdEqual(t.taskStatusId, TASK_STATUS.CANCELED)));
  }, [tasks, filters, dashboardFilters]);

  // The task read procedures return the file, client and vendor context with each task; resolve the service name
  // once per row so the grid can sort on the displayed values
  const taskRows = useMemo(() => (
    filteredTasks.map((task) => ({
      ...task,
      eeFullName: task.eeFullName || '-',
      clientName: task.clientName || '-',
      serviceName: allServices.find(s => s.id === task.serviceId)?.title || '-',
      vendorName: task.vendorName || '-',
    }))
  ), [filteredTasks, allServices]);

  // Filter change handlers
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleTaskTypeChange = (event) => {
    handleFilterChange({ taskTypeId: event.target.value || null });
  };

  const handleTaskStatusChange = (event) => {
    handleFilterChange({ taskStatusId: event.target.value });
  };

  const handleImportanceChange = (event) => {
    handleFilterChange({ taskImportanceId: event.target.value || null });
  };

  const handleServiceChange = (event) => {
    handleFilterChange({ serviceId: event.target.value || null });
  };

  const handleVIPChange = (event) => {
    handleFilterChange({ isVIP: event.target.checked || null });
  };

  const handleVendorChange = (event, newValue) => {
    handleFilterChange({ vendorId: newValue?.vendorId || null });
  };

  const handleDueDateChange = (event) => {
    handleFilterChange({ dueDate: event.target.value || null });
  };

  const handleShowCompletedChange = (event) => {
    handleFilterChange({ showCompleted: event.target.checked });
  };

  const handleShowCanceledChange = (event) => {
    handleFilterChange({ showCanceled: event.target.checked });
  };

  const handleClearFilters = () => {
    setFilters({
      taskTypeId: null,
      vendorId: null,
      isVIP: null,
      taskStatusId: TASK_STATUS.ACTIVE,
      taskImportanceId: null,
      serviceId: null,
      dueDate: null,
      showCompleted: false,
      showCanceled: false,
    });
  };

  // Handle task title click - determine action based on task type
  const handleTitleClick = (task) => {
    const taskTypeTitle = taskTypes.find(t => t.id === task.taskTypeId)?.title;

    // Call/VendorCall (1, 2) - Open call modal
    if (taskTypeTitle === 'Call' || taskTypeTitle === 'VendorCall') {
      onTaskAction(task, 'call');
    }
    // Email/VendorEmail (3, 4) - Open email editor
    else if (taskTypeTitle === 'Email' || taskTypeTitle === 'VendorEmail') {
      onTaskAction(task, 'email');
    }
    // Letter/VendorLetter (5, 7) - Open letter/email editor
    else if (taskTypeTitle === 'Letter' || taskTypeTitle === 'VendorLetter') {
      onTaskAction(task, 'letter');
    }
    // Voucher/VendorVoucher (6, 8) - Navigate to voucher page
    else if (taskTypeTitle === 'Voucher' || taskTypeTitle === 'VendorVoucher') {
      onTaskAction(task, 'voucher');
    }
  };

  // Get status text with color
  const getStatusText = (task) => {
    const statusClasses = {
      [TASK_STATUS.OVERDUE]: classes.statusOverdue,
      [TASK_STATUS.CANCELED]: classes.statusCanceled,
      [TASK_STATUS.COMPLETED]: classes.statusCompleted,
    };
    const statusId = Object.keys(TASK_STATUS_TITLES).find(id => isIdEqual(id, task.taskStatusId));

    return (
      <Typography variant="body2" className={statusClasses[statusId] || classes.statusActive}>
        {statusId ? TASK_STATUS_TITLES[statusId] : '-'}
      </Typography>
    );
  };

  // Get importance text with color
  const getImportanceText = (task) => {
    const level = getImportanceLevel(task);
    return (
      <Typography variant="body2" className={classes[level.classKey]}>
        {level.label}
      </Typography>
    );
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Task',
      Cell: ({ row }) => (
        <Link
          component="button"
          variant="body2"
          className={classes.clickableTitle}
          onClick={() => handleTitleClick(row.original)}
        >
          {row.original.title || 'Untitled Task'}
        </Link>
      ),
    },
    {
      accessorKey: 'fileNumber',
      header: 'File #',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.fileNumber}
        </a>
      ),
    },
    { accessorKey: 'eeFullName', header: 'Employee' },
    { accessorKey: 'clientName', header: 'Client' },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      Cell: ({ row }) => <TaskDueDate task={row.original} />,
    },
    {
      ...enumColumn('taskStatusId', TASK_STATUS_OPTIONS),
      header: 'Status',
      Cell: ({ row }) => getStatusText(row.original),
    },
    {
      // Filters and sorts on the label the cell shows, like an enumColumn
      accessorKey: 'taskImportanceId',
      accessorFn: task => getImportanceLevel(task).label,
      header: 'Importance',
      Cell: ({ row }) => getImportanceText(row.original),
    },
    { accessorKey: 'serviceName', header: 'Service' },
    { accessorKey: 'vendorName', header: 'Vendor' },
  ];

  const selectedVendor = vendors.find(v => v.vendorId === filters.vendorId) || null;

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noTasks} color="textSecondary">
          Loading tasks...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filter UI */}
      <Box className={classes.filtersContainer}>
        <FormControl className={classes.filterControlSmall} size="small">
          <InputLabel>Task Type</InputLabel>
          <Select
            value={filters.taskTypeId || ''}
            onChange={handleTaskTypeChange}
            disabled={isLoading}
            label="Task Type"
          >
            <MenuItem value="">All</MenuItem>
            {taskTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>{type.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterControlSmall} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.taskStatusId}
            onChange={handleTaskStatusChange}
            disabled={isLoading}
            label="Status"
          >
            <MenuItem value={ALL_STATUSES}>All</MenuItem>
            {TASK_STATUS_FILTER_OPTIONS.map((status) => (
              <MenuItem key={status.id} value={status.id}>{status.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterControlSmall} size="small">
          <InputLabel>Importance</InputLabel>
          <Select
            value={filters.taskImportanceId || ''}
            onChange={handleImportanceChange}
            disabled={isLoading}
            label="Importance"
          >
            <MenuItem value="">All</MenuItem>
            {taskImportances.map((level) => (
              <MenuItem key={level.id} value={level.id}>{level.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.filterControlSmall} size="small">
          <InputLabel>Service</InputLabel>
          <Select
            value={filters.serviceId || ''}
            onChange={handleServiceChange}
            disabled={isLoading}
            label="Service"
          >
            <MenuItem value="">All</MenuItem>
            {services.map((service) => (
              <MenuItem key={service.id} value={service.id}>{service.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          className={classes.filterControl}
          options={vendors}
          getOptionLabel={(option) => option.companyName || ''}
          value={selectedVendor}
          onChange={handleVendorChange}
          disabled={isLoading}
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Vendor" />
          )}
        />

        <TextField
          className={classes.filterControlSmall}
          label="Due Date"
          type="date"
          value={filters.dueDate || ''}
          onChange={handleDueDateChange}
          disabled={isLoading}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={filters.isVIP || false}
              onChange={handleVIPChange}
              disabled={isLoading}
            />
          }
          label="VIP Only"
        />

        <FormControlLabel
          control={
            <Switch
              checked={filters.showCompleted}
              onChange={handleShowCompletedChange}
              disabled={isLoading}
              color="primary"
            />
          }
          label="Show All Completed"
        />

        <FormControlLabel
          control={
            <Switch
              checked={filters.showCanceled}
              onChange={handleShowCanceledChange}
              disabled={isLoading}
              color="primary"
            />
          }
          label="Show All Canceled"
        />

        <Button onClick={handleClearFilters} disabled={isLoading} size="small">
          Clear Filters
        </Button>
      </Box>

      {/* Grid */}
      {filteredTasks.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noTasks} color="textSecondary">
            No tasks to display
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.tableContainer}>
          <DashboardDataTable
            columns={columns}
            data={taskRows}
            muiTableBodyRowProps={({ row }) => (
              taskStatuses.find(s => s.id === row.original.taskStatusId)?.title === 'Overdue'
                ? { className: classes.overdueRow }
                : {}
            )}
          />
        </Box>
      )}
    </>
  );
};

TasksGrid.propTypes = {
  tasks: PropTypes.array.isRequired,
  onTaskAction: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  vendors: PropTypes.array,
  enumerations: PropTypes.array,
  dashboardFilters: PropTypes.shape({
    clientId: PropTypes.number,
    fileId: PropTypes.number,
  }),
};

TasksGrid.defaultProps = {
  isLoading: false,
  vendors: [],
  enumerations: [],
  dashboardFilters: {},
};

export default TasksGrid;
